<template>
  <div class="mb-4 flex justify-end py-2">
    <div class="flex justify-between space-x-2">
      <DateFilter
        :model-value="globalQuery"
        :time-period-label="timePeriodLabel"
        range-type="custom"
        @update:model-value="setGlobalQuery"
      />
    </div>
  </div>
  <BaseCard class="mb-4">
    <div
      class="mb-2 flex w-full flex-wrap items-center justify-end space-x-2 pt-2"
    >
      <div class="mb-2 flex grow items-center space-x-2">
        <div
          class="grid w-full grid-cols-2 gap-2 md:grid-cols-4"
        >
          <ReportCashierFilter
            v-model="localQueries[0].cashierId"
          />
          <ReportDeviceFilter
            v-model="localQueries[0].deviceId"
          />
          <ReportFilterSelect
            v-model="localQueries[0].categoryId"
            label="Category"
            :options="categoryOptions"
            class="w-full"
          />
          <!-- ref: hospUnhide -->
          <ReportFilterSelect
            v-model="localQueries[0].productTagId"
            label="Product Tag"
            :options="productTagOptions"
            class="w-full"
          />
        </div>
      </div>
    </div>
  </BaseCard>
  <BaseCard class="relative">
    <QueryLoading
      v-if="showTopProductsReportLoading"
      class="min-h-96"
    />
    <QueryError
      v-else-if="showTopProductsReportError"
      class="min-h-96"
      :show-retry="true"
      @retry="refetchTopProductsReport"
    >
      Failed to retrieve top products report
    </QueryError>
    <div v-else class="flex w-full flex-col">
      <div class="flex items-center justify-between py-2">
        <ReportFilterLimit v-model="limit" class="w-full" />
        <ToggleSwitch
          v-model="localQueries[0].compareBy"
          :options="reportViewOptions"
          selected-option-color="primary"
          text-size="sm"
        />
      </div>
      <div
        class="h-64 w-full overflow-hidden sm:h-96 md:h-[32rem]"
      >
        <template
          v-if="localQueries[0].compareBy === 'count'"
        >
          <ChartBarCategory
            id="top-products-soldcount-graph"
            :labels="labels"
            :datasets="soldCountDatasets"
            x-axis-type="number"
            :currency="tenant.currency"
            @hover-datapoint="handleHoverDatapoint"
          />
        </template>
        <template
          v-else-if="
            localQueries[0].compareBy === 'revenue'
          "
        >
          <ChartBarCategory
            id="top-products-revenue-graph"
            :labels="labels"
            :datasets="revenueDatasets"
            x-axis-type="currency"
            :currency="tenant.currency"
            @hover-datapoint="handleHoverDatapoint"
          />
        </template>
      </div>

      <div
        class="shrink-0 rounded-lg bg-background-fg/5 p-2"
      >
        <template v-if="mainDataset">
          <div
            class="grid grid-cols-1 gap-2 overflow-hidden sm:grid-cols-2"
          >
            <template
              v-for="(item, index) in mainDataset"
              :key="index"
            >
              <div
                :class="[
                  'grid grid-cols-4 gap-4',
                  hoveredData?.index === undefined ||
                  hoveredData?.index === index
                    ? ''
                    : 'opacity-50',
                ]"
              >
                <div
                  class="col-span-2 flex items-center space-x-2"
                >
                  <ColorItem
                    rounded
                    :value="getColorForIndex(index)"
                  />
                  <p
                    class="line-clamp-2 text-sm font-medium"
                  >
                    {{ item.productName }}
                  </p>
                </div>

                <div>
                  <p
                    class="text-center text-sm leading-tight text-background-fg/70"
                  >
                    Sold
                  </p>
                  <p
                    class="line-clamp-1 text-center font-semibold"
                  >
                    {{ item.soldCount }}
                  </p>
                </div>
                <div>
                  <p
                    class="text-center text-sm leading-tight text-background-fg/70"
                  >
                    Revenue
                  </p>
                  <p
                    class="line-clamp-1 text-center font-semibold"
                  >
                    {{
                      formatToMerchantCurrencyString(
                        item.revenue,
                        {
                          compact: true,
                        }
                      )
                    }}
                  </p>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>
  </BaseCard>
</template>

<script setup lang="ts">
import type { TopProductsReportQuery } from "@/services/reports/retrieveTopProductsReport"
import type { HoveredDataPoint } from "@/components/ChartLineDate.vue"

const { tenant } = useTenant()
const { asOptions: categoryOptions } = useCategories()
const { asOptions: productTagOptions } = useProductTags()
const { formatToMerchantCurrencyString } = useCurrency()
const { options: colorOptions } = useColorOptions()

const emptyLocalQuery = {
  cashierId: undefined,
  categoryId: undefined,
  compareBy: "count",
} as const

const limit = ref(10)

const {
  apiQueries,
  localQueries,
  globalQuery,
  setGlobalQuery,
  timePeriodLabel,
} = useReportQuery<TopProductsReportQuery>(emptyLocalQuery)

const queryResults = useTopProductsReports(apiQueries)

const rawDatasets = computed(() =>
  queryResults.map(({ data }) =>
    data ? data.dataset.slice(0, limit.value) : undefined
  )
)

const showTopProductsReportLoading = computed(() =>
  queryResults.some((query) => query.isLoading)
)

const showTopProductsReportError = computed(() =>
  queryResults.some((query) => query.isError)
)

const refetchTopProductsReport = async () => {
  if (queryResults.length === 0) return
  await Promise.all(
    queryResults.map((query) => query.refetch())
  )
}

const mainDataset = computed(() => rawDatasets.value[0])
const labels = computed(
  () =>
    rawDatasets.value?.[0]?.map(
      ({ productName }) => productName
    ) || []
)

const soldCountDatasets = computed(() =>
  rawDatasets.value.map((rawDataset) => {
    return {
      label: "",
      data:
        rawDataset?.map(({ soldCount }) => soldCount) || [],
      color: undefined,
    }
  })
)

const revenueDatasets = computed(() =>
  rawDatasets.value.map((rawDataset) => {
    return {
      label: "",
      data: rawDataset?.map(({ revenue }) => revenue) || [],
      color: undefined,
    }
  })
)

const handleHoverDatapoint = (
  hoveredData_: HoveredDataPoint
) => {
  hoveredData.value = hoveredData_
}

const hoveredData = ref<HoveredDataPoint>()

const reportViewOptions = [
  {
    value: "count",
    label: "Count",
  },
  {
    value: "revenue",
    label: "Revenue",
  },
] as const

const getColorForIndex = (index: number) => {
  const numberOfColors = colorOptions.length
  const colorIndex = index % numberOfColors
  return `rgb(var(${colorOptions[colorIndex].value}))`
}
</script>
